import cn from 'classnames';
import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { object, string, boolean } from 'yup';
import { useFormik, FormikHelpers, FormikErrors } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';

import { captureException } from '@sentry/react';
import { postShareFriend } from '@/api';
import { noop } from '@/utils';
import { submitHandler } from '@/utils/forms';
import { AgreementContent } from '@/types/common';

import Button from '@/components/UI/Button';
import InputBool from '@/components/UI/InputBool';
import TextField from '@/components/UI/InputText';
import SROnly from '@/components/UI/SROnly';

export interface FriendFormContent {
  title?: string;
  your_name: string;
  your_email: string;
  friend_name: string;
  friend_email: string;
  agreement?: AgreementContent;
  button: string;
  message: string;
}

interface FriendFormValues {
  your_name: string;
  your_email: string;
  friend_name: string;
  friend_email: string;
  agreement: boolean;
}

interface FriendFormProps {
  lang: string;
  objectId: string;
  content: FriendFormContent;
  recaptchaSiteKey?: string;
  recaptchaDisabled?: boolean;
  onSuccess?: () => void;
}

const validationSchema = object().shape({
  your_name: string().max(128).required(),
  your_email: string().email().required(),
  friend_name: string().max(128).required(),
  friend_email: string().email().required(),
  agreement: boolean().oneOf([true])
});

const initialValues: FriendFormValues = {
  your_name: '',
  your_email: '',
  friend_name: '',
  friend_email: '',
  agreement: false
};

const TIMEOUT = 10000;

const FriendForm = ({
  lang,
  objectId,
  content,
  recaptchaSiteKey,
  recaptchaDisabled,
  onSuccess: onSuccessProps = noop
}: FriendFormProps) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [message, setMessage] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSuccessMessage = useCallback(() => {
    if (content.message) {
      setMessage(content.message);

      timeoutRef.current = setTimeout(() => {
        setMessage('');
        timeoutRef.current = null;
      }, TIMEOUT);
    }
  }, [content]);

  const onSuccess = useCallback((_values: FriendFormValues, helper: FormikHelpers<FriendFormValues>) => {
    helper.resetForm();
    showSuccessMessage();
    onSuccessProps();
  }, [onSuccessProps, showSuccessMessage]);

  const {
    errors,
    isSubmitting,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit
  } = useFormik<FriendFormValues>({
    initialValues,
    validationSchema,
    onSubmit: async(form, helper) => {
      let recaptchaToken = '';

      if (recaptchaSiteKey && !recaptchaDisabled) {
        try {
          recaptchaToken = await recaptchaRef.current!.executeAsync() ?? '';
          recaptchaRef.current!.reset();
        } catch (e) {
          captureException(e, { extra: { context: 'recaptcha', form: 'FriendForm' } });
          helper.setErrors({ nonFieldError: 'reCAPTCHA error, please try again' } as FormikErrors<FriendFormValues>);
          helper.setSubmitting(false);
          return Promise.resolve();
        }
      }

      const payload = { recaptcha_token: recaptchaToken, ...form };
      const onSend = () => postShareFriend(payload, lang, objectId);
      return submitHandler(form, helper, onSend, onSuccess);
    }
  });

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const { nonFieldError } = errors as Record<string, string | undefined>;

  return (
    <form
      className="form friend-form"
      onSubmit={handleSubmit}
    >
      <div className="form-field">
        <SROnly
          component="label"
          htmlFor="your_name"
        >
          {content.your_name}
        </SROnly>

        <TextField
          autoComplete="off"
          className={cn({ error: touched.your_name && errors.your_name })}
          id="your_name"
          name="your_name"
          placeholder={content.your_name}
          value={values.your_name}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>

      <div className="form-field">
        <SROnly
          component="label"
          htmlFor="your_email"
        >
          {content.your_email}
        </SROnly>

        <TextField
          autoComplete="off"
          className={cn({ error: touched.your_email && errors.your_email })}
          id="your_email"
          name="your_email"
          placeholder={content.your_email}
          value={values.your_email}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>

      <div className="form-field">
        <SROnly
          component="label"
          htmlFor="friend_name"
        >
          {content.friend_name}
        </SROnly>

        <TextField
          autoComplete="off"
          className={cn({ error: touched.friend_name && errors.friend_name })}
          id="friend_name"
          name="friend_name"
          placeholder={content.friend_name}
          value={values.friend_name}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>

      <div className="form-field">
        <SROnly
          component="label"
          htmlFor="friend_email"
        >
          {content.friend_email}
        </SROnly>

        <TextField
          autoComplete="off"
          className={cn({ error: touched.friend_email && errors.friend_email })}
          id="friend_email"
          name="friend_email"
          placeholder={content.friend_email}
          value={values.friend_email}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>

      <div className="form-field">
        <InputBool
          className={cn({ error: touched.agreement && errors.agreement })}
          id="agreement"
          name="agreement"
          label={(
            <>
              {content.agreement?.label}
              {' '}
              <a
                href={content.agreement?.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {content.agreement?.page}
              </a>
              {' '}
              {content.agreement?.site}
            </>
          )}
          checked={values.agreement}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>

      {!!recaptchaSiteKey && !recaptchaDisabled && (
        <div className="form-field">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            size="invisible"
          />
        </div>
      )}

      {!!message && (
        <div className="form-message">
          {message}
        </div>
      )}

      {!!nonFieldError && (
        <div className="form-error">
          {nonFieldError}
        </div>
      )}

      <div className="form-button">
        <Button
          className="gold-solid"
          disabled={isSubmitting}
          type="submit"
        >
          {content.button}
        </Button>
      </div>

      {!!recaptchaSiteKey && !recaptchaDisabled && (
        <p className="form-recaptcha-notice">
          {'This site is protected by reCAPTCHA and the Google '}
          <a
            href="https://policies.google.com/privacy"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>
          {' and '}
          <a
            href="https://policies.google.com/terms"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </a>
          {' apply.'}
        </p>
      )}
    </form>
  );
};

export default memo(FriendForm);
